import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Avatar } from '../../components/ui/Avatar.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { API_BASE_URL } from '../../config.js';
import { useAuth } from '../../context/AuthContext.js';
import { api } from '../../services/api.js';
import {
  PASSWORD_INVALID_MESSAGE,
  PASSWORD_RULE_TEXT,
  satisfiesPasswordPolicy,
} from '../../utils/passwordPolicy.js';
import { IdentityDocuments } from './IdentityDocuments.js';

interface MyProfile {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  date_of_birth?: string | null;
  status?: string;
}

interface ProfileForm {
  first_name: string;
  last_name: string;
  phone: string;
  gender: string;
  date_of_birth: string;
}

const phoneRegex = /^\+?[0-9][0-9\s-]{4,}$/;

function toForm(p: MyProfile): ProfileForm {
  return {
    first_name: p.first_name ?? '',
    last_name: p.last_name ?? '',
    phone: p.phone ?? '',
    gender: p.gender ?? '',
    date_of_birth: p.date_of_birth ? p.date_of_birth.substring(0, 10) : '',
  };
}

function validate(form: ProfileForm): Partial<Record<keyof ProfileForm, string>> {
  const errors: Partial<Record<keyof ProfileForm, string>> = {};
  if (!form.first_name.trim()) errors.first_name = 'First name is required';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required';
  if (form.phone && !phoneRegex.test(form.phone)) errors.phone = 'Invalid phone number';
  if (form.gender && !['male', 'female', 'other'].includes(form.gender))
    errors.gender = 'Invalid gender';
  if (form.date_of_birth) {
    const d = new Date(form.date_of_birth);
    if (Number.isNaN(d.getTime()) || d.getTime() > Date.now())
      errors.date_of_birth = 'Date of birth cannot be in the future';
  }
  return errors;
}

interface PasswordForm {
  currentPw: string;
  newPw: string;
  confirmPw: string;
}

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const {
    data: profile,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api.get<MyProfile>('/users/me'),
  });

  const [form, setForm] = useState<ProfileForm | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof ProfileForm, string>>>({});
  const [saved, setSaved] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null,
  );

  const [pwForm, setPwForm] = useState<PasswordForm>({ currentPw: '', newPw: '', confirmPw: '' });
  const [pwErrors, setPwErrors] = useState<Partial<Record<keyof PasswordForm | 'server', string>>>(
    {},
  );
  const [pwSaved, setPwSaved] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });

  const pwMutation = useMutation({
    mutationFn: () =>
      api.patch('/users/me/password', {
        current_password: pwForm.currentPw,
        new_password: pwForm.newPw,
        confirm_password: pwForm.confirmPw,
      }),
    onSuccess: () => {
      setPwForm({ currentPw: '', newPw: '', confirmPw: '' });
      setPwErrors({});
      setPwSaved(true);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Password change failed';
      setPwErrors({ server: msg });
    },
  });

  function handlePwSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPwSaved(false);
    const errs: Partial<Record<keyof PasswordForm | 'server', string>> = {};
    if (!pwForm.currentPw) errs.currentPw = 'Current password is required';
    if (!pwForm.newPw) errs.newPw = 'New password is required';
    // 140 US3: the rule the server has enforced here since 035. Checked before submitting so the
    // administrator is not told by a refusal that displays as the bare word "ValidationError".
    else if (!satisfiesPasswordPolicy(pwForm.newPw)) errs.newPw = PASSWORD_INVALID_MESSAGE;
    if (pwForm.newPw && pwForm.newPw !== pwForm.confirmPw)
      errs.confirmPw = 'Passwords do not match';
    setPwErrors(errs);
    if (Object.keys(errs).length > 0) return;
    pwMutation.mutate();
  }

  useEffect(() => {
    if (profile) setForm(toForm(profile));
  }, [profile]);

  const mutation = useMutation({
    mutationFn: (data: ProfileForm) => api.patch<MyProfile>('/users/me', data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['my-profile'], updated);
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
      setForm(toForm(updated));
      setSaved(true);
    },
  });

  function setField(key: keyof ProfileForm, value: string) {
    setSaved(false);
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form) return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    mutation.mutate(form);
  }

  function handleCancel() {
    if (profile) setForm(toForm(profile));
    setErrors({});
    setSaved(false);
  }

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setAvatarMsg(null);
    setIsUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await fetch(`${API_BASE_URL}/users/me/avatar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        setAvatarMsg({ type: 'error', text: payload.error ?? 'Upload failed' });
      } else {
        await refreshUser();
        setAvatarMsg({ type: 'success', text: 'Avatar updated.' });
      }
    } catch {
      setAvatarMsg({ type: 'error', text: 'Upload failed. Please try again.' });
    } finally {
      setIsUploading(false);
      e.currentTarget.value = '';
    }
  }

  async function handleAvatarRemove() {
    setAvatarMsg(null);
    setIsUploading(true);
    try {
      await api.delete('/users/me/avatar');
      await refreshUser();
      setAvatarMsg({ type: 'success', text: 'Photo removed.' });
    } catch {
      setAvatarMsg({ type: 'error', text: 'Remove failed. Please try again.' });
    } finally {
      setIsUploading(false);
    }
  }

  const roleNames = (user?.roles ?? []).map((r) => r.name).join(', ') || '—';
  const fullName = profile ? `${profile.first_name} ${profile.last_name}`.trim() : '';

  if (isError) {
    return (
      <div>
        <PageHeader title="Profile" description="Your account" />
        <p className="text-sm text-red-600">
          Could not load your profile. Your session may have expired — try signing in again.
        </p>
      </div>
    );
  }

  if (isLoading || !form) {
    return (
      <div>
        <PageHeader title="Profile" description="Your account" />
        <p className="text-sm text-steel">Loading…</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Profile" description="Your account" />
      <div className="max-w-2xl space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Account</h2>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <div className="group relative w-fit">
                <Avatar
                  name={fullName || profile?.email || '?'}
                  src={user?.avatar_url ?? undefined}
                  size="lg"
                  className={isUploading ? 'opacity-50' : ''}
                />
                <label
                  htmlFor="avatar-upload"
                  className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <Camera className="h-5 w-5 text-white" />
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  onChange={handleAvatarChange}
                  disabled={isUploading}
                />
              </div>
              {user?.avatar_url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleAvatarRemove}
                  disabled={isUploading}
                >
                  Remove photo
                </Button>
              )}
              {avatarMsg && (
                <p
                  className={`text-xs ${avatarMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                >
                  {avatarMsg.text}
                </p>
              )}
            </div>
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs font-medium text-steel">Email</p>
                <p className="text-sm text-ink">{profile?.email}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-steel">Role</p>
                <p className="text-sm text-ink">{roleNames}</p>
              </div>
              {profile?.status && (
                <div>
                  <p className="text-xs font-medium text-steel">Status</p>
                  <p className="text-sm text-ink capitalize">{profile.status}</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Personal details</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="First name" name="first_name" required error={errors.first_name}>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) => setField('first_name', e.currentTarget.value)}
                />
              </FormField>
              <FormField label="Last name" name="last_name" required error={errors.last_name}>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => setField('last_name', e.currentTarget.value)}
                />
              </FormField>
              <FormField label="Phone" name="phone" hint="Optional" error={errors.phone}>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.currentTarget.value)}
                />
              </FormField>
              <FormField label="Gender" name="gender" error={errors.gender}>
                <Select
                  id="gender"
                  value={form.gender}
                  onChange={(e) => setField('gender', e.currentTarget.value)}
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Select>
              </FormField>
              <FormField label="Date of birth" name="date_of_birth" error={errors.date_of_birth}>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setField('date_of_birth', e.currentTarget.value)}
                />
              </FormField>
            </div>
          </Card>

          {saved && <p className="text-sm text-green-600">Profile updated.</p>}
          {mutation.error instanceof Error && (
            <p className="text-sm text-red-600">{mutation.error.message}</p>
          )}

          <div className="flex gap-3">
            <Button type="submit" loading={mutation.isPending}>
              Save Changes
            </Button>
            <Button type="button" variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>

        <IdentityDocuments />

        <form onSubmit={handlePwSubmit}>
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Change Password</h2>
            <div className="space-y-4">
              <FormField label="Current password" name="currentPw" error={pwErrors.currentPw}>
                <div className="relative">
                  <Input
                    id="currentPw"
                    type={showPw.current ? 'text' : 'password'}
                    value={pwForm.currentPw}
                    onChange={(e) => {
                      setPwSaved(false);
                      setPwForm({ ...pwForm, currentPw: e.currentTarget.value });
                    }}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center text-steel hover:text-ink"
                    onClick={() => setShowPw((s) => ({ ...s, current: !s.current }))}
                    aria-label={showPw.current ? 'Hide password' : 'Show password'}
                  >
                    {showPw.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>

              <FormField
                label="New password"
                name="newPw"
                error={pwErrors.newPw}
                hint={PASSWORD_RULE_TEXT}
              >
                <div className="relative">
                  <Input
                    id="newPw"
                    type={showPw.new ? 'text' : 'password'}
                    value={pwForm.newPw}
                    onChange={(e) => {
                      setPwSaved(false);
                      setPwForm({ ...pwForm, newPw: e.currentTarget.value });
                    }}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center text-steel hover:text-ink"
                    onClick={() => setShowPw((s) => ({ ...s, new: !s.new }))}
                    aria-label={showPw.new ? 'Hide password' : 'Show password'}
                  >
                    {showPw.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>

              <FormField label="Confirm new password" name="confirmPw" error={pwErrors.confirmPw}>
                <div className="relative">
                  <Input
                    id="confirmPw"
                    type={showPw.confirm ? 'text' : 'password'}
                    value={pwForm.confirmPw}
                    onChange={(e) => {
                      setPwSaved(false);
                      setPwForm({ ...pwForm, confirmPw: e.currentTarget.value });
                    }}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center text-steel hover:text-ink"
                    onClick={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))}
                    aria-label={showPw.confirm ? 'Hide password' : 'Show password'}
                  >
                    {showPw.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>
            </div>

            {pwSaved && <p className="mt-3 text-sm text-green-600">Password updated.</p>}
            {pwErrors.server && <p className="mt-3 text-sm text-red-600">{pwErrors.server}</p>}

            <div className="mt-4">
              <Button type="submit" loading={pwMutation.isPending}>
                Update Password
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
}
